import axios from 'axios';
import i18n from 'i18n-js';
import {getAuthHeaders, getBaseUrl} from '../helpers/rulesApiHelpers';
import {buildError, classifyFailure, firstMessage} from './adminTenantsActions';
import {
  buildSettingsPatch,
  buildWriteRequest,
  legacyRouteOf
} from '../components/adminTools/tenantPage/sectionConfig';
import {
  addonPriceOf,
  billingImpactOf,
  billsThroughStripe,
  subscriptionCents
} from '../components/adminTools/tenantPage/billingImpact';

export const ADMIN_TENANT_PAGE_REQUEST = 'ADMIN_TENANT_PAGE_REQUEST';
export const ADMIN_TENANT_PAGE_SUCCESS = 'ADMIN_TENANT_PAGE_SUCCESS';
export const ADMIN_TENANT_PAGE_FAILURE = 'ADMIN_TENANT_PAGE_FAILURE';

export const ADMIN_TENANT_PAGE_SAVE_STARTED = 'ADMIN_TENANT_PAGE_SAVE_STARTED';
export const ADMIN_TENANT_PAGE_SETTING_SAVED =
  'ADMIN_TENANT_PAGE_SETTING_SAVED';
export const ADMIN_TENANT_PAGE_REREAD_SUCCESS =
  'ADMIN_TENANT_PAGE_REREAD_SUCCESS';
export const ADMIN_TENANT_PAGE_REREAD_FAILURE =
  'ADMIN_TENANT_PAGE_REREAD_FAILURE';
export const ADMIN_TENANT_PAGE_NOTICE_DISMISSED =
  'ADMIN_TENANT_PAGE_NOTICE_DISMISSED';

const MAX_REREAD_ATTEMPTS = 3;

let latestRequestId = 0;
let loadEpoch = 0;
let saveEpoch = 0;
let rereadSeq = 0;
let showWanted = false;

const classify = (caught, endpoint) => {
  const status = caught && caught.response && caught.response.status;
  const failure =
    status === 404
      ? buildError(
          'notFound',
          status,
          firstMessage(caught.response.data, 'Not found')
        )
      : classifyFailure(caught);
  return Object.assign(failure, {endpoint});
};

const load = async (url, headers, isValid) => {
  let response;
  try {
    response = await axios.get(url, {headers});
  } catch (caught) {
    throw classify(caught, url);
  }
  const body = response.data;
  if (body && body.status === false) {
    throw Object.assign(
      buildError(
        'rejected',
        response.status || null,
        firstMessage(body, 'The server rejected the request')
      ),
      {endpoint: url}
    );
  }
  if (!isValid(body)) {
    throw Object.assign(
      buildError(
        'server',
        response.status || null,
        'Unexpected response from the server'
      ),
      {endpoint: url}
    );
  }
  return body;
};

const loadShow = (base, tenantId, headers) =>
  load(
    `${base}/tenants/${tenantId}.json`,
    headers,
    body => Boolean(body) && Boolean(body.tenant)
  );

const isDeletedShow = show =>
  Boolean(
    show &&
      show.tenant &&
      show.tenant.basicinfo &&
      show.tenant.basicinfo.deleted_at
  );

const loadSettings = (base, tenantId, headers) =>
  load(
    `${base}/tenants/${tenantId}/settings.json`,
    headers,
    body => Boolean(body) && body.status === true
  );

const isServerError = failure =>
  typeof failure.status === 'number' && failure.status >= 500;

// /show.json answers 500, not 404, for a tenant id that does not exist; /settings.json is the
// call that says 404, so ask it before reporting a server error.
const loadShowOrNotFound = async (base, tenantId, headers) => {
  try {
    return await loadShow(base, tenantId, headers);
  } catch (showFailure) {
    if (!isServerError(showFailure)) throw showFailure;
    try {
      await loadSettings(base, tenantId, headers);
    } catch (settingsFailure) {
      if (settingsFailure.kind === 'notFound') throw settingsFailure;
    }
    throw showFailure;
  }
};

const readTenantPage = async tenantId => {
  const base = await getBaseUrl();
  const headers = await getAuthHeaders();
  // /settings.json 404s for a deleted tenant; reading /show.json first lets the page still
  // render its header and Restore action (GROOV-5187 review).
  const show = await loadShowOrNotFound(base, tenantId, headers);
  const settings = isDeletedShow(show)
    ? null
    : await loadSettings(base, tenantId, headers);
  return {settings, show};
};

const staleFailure = () =>
  buildError('stale', null, i18n.t('adminTenantPage.loadFailedBanner'));

export function fetchTenantPage(tenantId) {
  return async dispatch => {
    latestRequestId += 1;
    loadEpoch += 1;
    showWanted = false;
    const requestId = latestRequestId;
    dispatch({type: ADMIN_TENANT_PAGE_REQUEST, tenantId, requestId});
    const attemptRead = async attempt => {
      const savesAtStart = saveEpoch;
      const {settings, show} = await readTenantPage(tenantId);
      if (requestId !== latestRequestId) return {stale: true};
      // A save that finished while this read was in flight may not be in what was read, and
      // its SETTING_SAVED already updated the store, so read again rather than overwrite it.
      if (saveEpoch !== savesAtStart) {
        if (attempt === MAX_REREAD_ATTEMPTS) throw staleFailure();
        return attemptRead(attempt + 1);
      }
      dispatch({
        type: ADMIN_TENANT_PAGE_SUCCESS,
        tenantId,
        requestId,
        settings,
        show,
        loadedAt: Date.now()
      });
      return {settings, show};
    };
    try {
      return await attemptRead(1);
    } catch (caught) {
      if (requestId !== latestRequestId) return {stale: true};
      const failure = classifyFailure(caught);
      dispatch({
        type: ADMIN_TENANT_PAGE_FAILURE,
        tenantId,
        requestId,
        error: {
          kind: failure.kind,
          status: failure.status,
          message: failure.message,
          endpoint: failure.endpoint || null
        }
      });
      throw failure;
    }
  };
}

const joined = messages =>
  Array.isArray(messages) && messages.length > 0 ? messages.join('; ') : null;

const failureText = caught => {
  const response = caught && caught.response;
  const status = response && response.status;
  if (status === 401 || status === 403) {
    return i18n.t('adminTenantPage.forbidden', {
      defaultValue: 'Your account no longer has team access. Sign in again.'
    });
  }
  return (
    (response && joined(response.data && response.data.error_messages)) ||
    classifyFailure(caught).message
  );
};

const rejectionText = body =>
  joined(body && body.model_errors) ||
  joined(body && body.error_messages) ||
  i18n.t('adminTenantPage.saveRejected');

const readSettingsOutcome = async (tenantId, withShow) => {
  try {
    const base = await getBaseUrl();
    const headers = await getAuthHeaders();
    const [settings, show] = await Promise.all([
      loadSettings(base, tenantId, headers),
      withShow ? loadShow(base, tenantId, headers) : undefined
    ]);
    return {settings, show};
  } catch (caught) {
    return {failure: classifyFailure(caught)};
  }
};

const rereadSettings =
  (tenantId, attemptsLeft = MAX_REREAD_ATTEMPTS, withShow = false) =>
  async dispatch => {
    if (withShow) showWanted = true;
    const needShow = showWanted;
    if (attemptsLeft === 0) {
      showWanted = false;
      dispatch({
        type: ADMIN_TENANT_PAGE_REREAD_FAILURE,
        tenantId,
        error: {
          kind: 'stale',
          status: null,
          message: i18n.t('adminTenantPage.loadFailedBanner'),
          endpoint: null
        }
      });
      return {ok: false};
    }
    rereadSeq += 1;
    const seq = rereadSeq;
    const loads = loadEpoch;
    const saves = saveEpoch;
    const {settings, show, failure} = await readSettingsOutcome(
      tenantId,
      needShow
    );
    if (seq !== rereadSeq || loads !== loadEpoch) return {ok: false};
    if (saves !== saveEpoch)
      return dispatch(rereadSettings(tenantId, attemptsLeft - 1));
    showWanted = false;
    if (settings) {
      dispatch({
        type: ADMIN_TENANT_PAGE_REREAD_SUCCESS,
        tenantId,
        settings,
        show
      });
      return {ok: true, settings, show};
    }
    dispatch({
      type: ADMIN_TENANT_PAGE_REREAD_FAILURE,
      tenantId,
      error: {
        kind: failure.kind,
        status: failure.status,
        message: failure.message,
        endpoint: failure.endpoint || null
      }
    });
    return {ok: false};
  };

export const refreshTenantSettings = tenantId => rereadSettings(tenantId);

const rejectedByServer = caught =>
  Boolean(caught && caught.response && caught.response.status === 422);

const didNotSave = () => i18n.t('adminTenantPage.didNotSave');

const sameAmount = (a, b) => Number(a || 0) === Number(b || 0);

const basicinfoPatch = patch => ({show: {basicinfo: patch}});

const billingReportOf = body => ({
  applied:
    body && typeof body.billing_applied === 'boolean'
      ? body.billing_applied
      : undefined,
  error: (body && body.billing_error) || null
});

// update_price_field stores the price before it calls Stripe. GROOV-5248 backends report the Stripe outcome in
// billing_applied; older ones answer {} even when Stripe raised, so for them the only evidence is a new
// price[feature].stripe_id after an add, and a removal leaves no trace at all.
const addonBillingOf = (held, before, value, stripe, report) => {
  if (!stripe) return null;
  if (report.applied === false) return 'notApplied';
  if (report.applied === true) return value.toggle ? 'confirmed' : null;
  if (value.toggle) {
    if (!before) return 'notConfirmed';
    return held.stripe_id && held.stripe_id !== before.stripe_id
      ? 'confirmed'
      : 'notApplied';
  }
  return before.toggle ? 'notConfirmed' : null;
};

const freshPriceBefore = async (base, tenantId, headers, feature) => {
  try {
    return addonPriceOf(await loadShow(base, tenantId, headers), feature);
  } catch (caught) {
    return null;
  }
};

const addonOutcomeOf = (held, before, value, stripe, body) => {
  const report = billingReportOf(body);
  return {
    billing: addonBillingOf(held, before, value, stripe, report),
    billingError: report.error
  };
};

const legacyCall = async (tenantId, settingKey, legacy, value, context) => {
  const base = await getBaseUrl();
  const headers = await getAuthHeaders();
  const tenantUrl = `${base}/tenants/${tenantId}`;
  if (legacy.kind === 'price') {
    const before =
      value.toggle && context.stripe
        ? await freshPriceBefore(base, tenantId, headers, legacy.feature)
        : context.before;
    const response = await axios.post(
      `${tenantUrl}/update_price_field.json`,
      {feature: legacy.feature, value},
      {headers}
    );
    return {
      body: response.data,
      verify: async body => {
        let show;
        try {
          show = await loadShow(base, tenantId, headers);
        } catch (caught) {
          return {error: classifyFailure(caught).message};
        }
        const held =
          show.tenant.basicinfo &&
          show.tenant.basicinfo.price &&
          show.tenant.basicinfo.price[legacy.feature];
        if (
          !held ||
          Boolean(held.toggle) !== Boolean(value.toggle) ||
          !sameAmount(held.amount, value.amount)
        )
          return {error: didNotSave()};
        return {
          patch: basicinfoPatch({price: {[legacy.feature]: held}}),
          ...addonOutcomeOf(held, before, value, context.stripe, body)
        };
      }
    };
  }
  if (legacy.kind === 'workflow') {
    const response = await axios.get(
      `${base}/tenants/update_scan_workflow.json?tenant_id=${tenantId}&workflow=${encodeURIComponent(
        value
      )}`,
      {headers}
    );
    return {
      body: response.data,
      verify: async body =>
        body && body.workflow === value
          ? {patch: basicinfoPatch({scan_pack_workflow: value})}
          : {error: didNotSave()}
    };
  }
  if (legacy.kind === 'graphql') {
    const endpoint =
      legacy.endpoint || `update_${legacy.field.replace('use_', '')}`;
    const response = await axios.post(
      `${tenantUrl}/${endpoint}.json`,
      {enabled: value},
      {headers}
    );
    return {
      body: response.data,
      verify: async body =>
        body && body.status === true && body[legacy.field] === value
          ? {patch: basicinfoPatch({[legacy.field]: value})}
          : {error: didNotSave()}
    };
  }
  const response = await axios.put(
    `${tenantUrl}/post_scanning_config`,
    {psv2_enabled: value},
    {headers}
  );
  return {
    body: response.data,
    verify: async body =>
      body &&
      body.psv2_enabled === value &&
      (body.tenant_id === undefined ||
        Number(body.tenant_id) === Number(tenantId))
        ? {
            patch: {
              settings: {
                post_scanning_config: {
                  psv2_enabled: value,
                  modules_config: body.modules_config || []
                }
              }
            }
          }
        : {error: didNotSave()}
  };
};

const rereadable = caught => {
  const status = caught && caught.response && caught.response.status;
  return Boolean(status) && status !== 401 && status !== 403;
};

const saveLegacySetting = (tenantId, settingKey, legacy, value) => {
  return async (dispatch, getState) => {
    dispatch({type: ADMIN_TENANT_PAGE_SAVE_STARTED, tenantId});
    const failWith = async (error, reread) => {
      if (reread) await dispatch(rereadSettings(tenantId, undefined, true));
      return {ok: false, error};
    };
    const {show} = getState().adminTenantPage;
    const context =
      legacy.kind === 'price'
        ? {
            before: addonPriceOf(show, legacy.feature),
            stripe: billsThroughStripe(show)
          }
        : null;
    let outcome;
    try {
      outcome = await legacyCall(tenantId, settingKey, legacy, value, context);
    } catch (caught) {
      return failWith(failureText(caught), rereadable(caught));
    }
    const {body} = outcome;
    if (body && body.status === false)
      return failWith(rejectionText(body), true);
    const verdict = await outcome.verify(body);
    if (verdict.error) return failWith(verdict.error, true);
    saveEpoch += 1;
    if (getState().adminTenantPage.tenantId === tenantId) {
      dispatch({
        type: ADMIN_TENANT_PAGE_SETTING_SAVED,
        tenantId,
        settingKey,
        patch: (verdict.patch && verdict.patch.settings) || {},
        showPatch: verdict.patch && verdict.patch.show,
        sideEffects: []
      });
    }
    return {
      ok: true,
      sideEffects: [],
      billing: verdict.billing || null,
      billingError: verdict.billingError || null
    };
  };
};

// Before GROOV-5248, create_new_plan_and_assign set @updated_in_stripe ahead of the Stripe call and rescued a failed
// lookup without clearing it, so a recorded amount proves GroovePacker stored the price, not that Stripe applied
// it, and an amount that did not move is the only reliable evidence of failure. A backend that sends
// billing_applied is believed over the amount: false is a failure, and true with an unmoved amount means it did
// not reprice (it only does so for user-count limits), which is a plain save.
const repriceBillingOf = (reread, impact, reportedApplied) => {
  const cents = reread.ok ? subscriptionCents(reread.show) : null;
  if (reportedApplied && impact.reportsOutcome) return null;
  if (reportedApplied) return cents === impact.to * 100 ? 'recorded' : null;
  if (cents === null) return 'notConfirmed';
  return cents === impact.to * 100 ? 'recorded' : 'notApplied';
};

export function saveTenantSetting(tenantId, settingKey, value) {
  return async (dispatch, getState) => {
    const legacy = legacyRouteOf(settingKey);
    if (legacy)
      return dispatch(saveLegacySetting(tenantId, settingKey, legacy, value));
    const current = getState().adminTenantPage;
    const impact = billingImpactOf(settingKey, value, current);
    const request = buildWriteRequest(settingKey, value, current.settings);
    if (!request) {
      console.warn(`No save route for setting "${settingKey}"`);
      return {
        ok: false,
        error: i18n.t('adminTenantPage.noSaveRoute')
      };
    }
    dispatch({type: ADMIN_TENANT_PAGE_SAVE_STARTED, tenantId});
    let body;
    try {
      const base = await getBaseUrl();
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${base}/tenants/${tenantId}/${request.endpoint}`,
        request.body,
        {headers}
      );
      body = response.data;
    } catch (caught) {
      const error = failureText(caught);
      if (!rejectedByServer(caught)) return {ok: false, error};
      await dispatch(rereadSettings(tenantId));
      return {ok: false, error};
    }
    if (!body || body.status !== true) {
      const error = rejectionText(body);
      if (!body || body.status !== false) return {ok: false, error};
      await dispatch(rereadSettings(tenantId));
      return {ok: false, error};
    }
    const sideEffects = Array.isArray(body.side_effects)
      ? body.side_effects
      : [];
    const now = getState().adminTenantPage;
    saveEpoch += 1;
    if (now.tenantId === tenantId) {
      dispatch({
        type: ADMIN_TENANT_PAGE_SETTING_SAVED,
        tenantId,
        settingKey,
        patch: buildSettingsPatch(request, body, now.settings, sideEffects),
        sideEffects
      });
    }
    if (body.billing_applied === false)
      return {
        ok: true,
        sideEffects,
        settings: body.settings,
        billing: 'notApplied',
        billingKind: 'reprice',
        billingError: body.billing_error || null
      };
    if (!impact || impact.kind !== 'reprice')
      return {ok: true, sideEffects, settings: body.settings, billing: null};
    const reread = await dispatch(rereadSettings(tenantId, undefined, true));
    return {
      ok: true,
      sideEffects,
      settings: body.settings,
      billing: repriceBillingOf(reread, impact, body.billing_applied === true),
      billingKind: 'reprice',
      repricedTo: {amount: impact.to, interval: impact.interval}
    };
  };
}

export const dismissSideEffects = () => ({
  type: ADMIN_TENANT_PAGE_NOTICE_DISMISSED
});
